/**
 * Stock Requests Page — Branch A creates requests, Branch B triages them
 * into BTOs and DRAFT POs.
 *
 * MVP shape (Feb 2026):
 *  • Top tabs: Inbox (incoming) | Outbox (outgoing) | New Request
 *  • New Request: product search reuses sales pattern, shows BOTH branches'
 *    on-hand qty so the requesting branch can see what's actually available.
 *  • Triage: per-line dropdown {Personal Transfer | Supplier PO | Unfulfilled}
 *    with supplier picker + unit_price for supplier_po rows.
 *  • Detail panel shows linked child docs (BTO + POs) with status.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import StockMovementSwitcher from '../components/StockMovementSwitcher';
import { formatPHP, fmtDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ClipboardList, Plus, Send, ArrowRight,
         Truck, X, Check, RefreshCw, FileText, AlertTriangle, Printer, QrCode, Copy } from 'lucide-react';
import { toast } from 'sonner';
import SmartProductSearch from '../components/SmartProductSearch';
import CalcInput from '../components/CalcInput';
import PrintEngine from '../lib/PrintEngine';
import SendToPrintModal from '../components/SendToPrintModal';

const STATUS_BADGE = {
  draft:                   { label: 'Draft',           cls: 'bg-slate-100 text-slate-700' },
  sent:                    { label: 'Awaiting Triage', cls: 'bg-amber-100 text-amber-700' },
  fulfillment_generated:   { label: 'In Fulfillment',  cls: 'bg-sky-100 text-sky-700' },
  completed:               { label: 'Completed',       cls: 'bg-emerald-100 text-emerald-700' },
  // Audit ORD-2: fulfillment that left lines short/missing/unfulfilled must
  // never read as a clean green 'Completed'. Owner 2026-09-03: ONE label for
  // both stories (decided at triage, or short on receipt) — "Partially
  // fulfilled" is the request's permanent status; the requester re-orders.
  completed_with_variance: { label: 'Partially fulfilled', cls: 'bg-rose-100 text-rose-700' },
  cancelled:               { label: 'Cancelled',       cls: 'bg-rose-100 text-rose-700' },
};

// Lines the supplying branch said it cannot fulfill at triage.
const unfulfilledCount = (items) =>
  (items || []).filter(i => i.status === 'unfulfilled' || i.fulfillment_type === 'unfulfilled').length;

// Derived headline: an In-Fulfillment request with any "can't fulfill" line is
// already PARTIALLY FULFILLED (owner: as good as finalized) — say so, with the
// count, instead of the generic "In Fulfillment". Raw status is untouched so
// the completion logic keeps working.
const requestStatusView = (status, items) => {
  if (status === 'fulfillment_generated') {
    const n = unfulfilledCount(items);
    if (n > 0) {
      const total = (items || []).length;
      return { label: `Partially fulfilled · ${total - n} of ${total} lines`, cls: 'bg-rose-100 text-rose-700', partial: true };
    }
  }
  return { ...(STATUS_BADGE[status] || { label: status, cls: 'bg-slate-100 text-slate-700' }), partial: false };
};

const VARIANCE_BADGE = {
  completed:        { label: 'Completed',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  under_delivered:  { label: 'Under-delivered', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  over_delivered:   { label: 'Over-delivered',  cls: 'bg-sky-100 text-sky-700 border-sky-200' },
  extra_items:      { label: 'Extra items',     cls: 'bg-violet-100 text-violet-700 border-violet-200' },
  missing_items:    { label: 'Missing items',   cls: 'bg-rose-100 text-rose-700 border-rose-200' },
};

const StatusBadge = ({ status, items }) => {
  const s = requestStatusView(status, items);
  return <Badge className={`${s.cls} text-[10px] font-medium`} data-testid="sr-status-badge">{s.label}</Badge>;
};

// Requests needing action first, terminal ones sink; newest first within each
// group so stale ignored requests don't bury fresh ones.
const SR_URGENCY = {
  // Audit ORD-2: discrepancy outcomes rank tier 1 (owner ordering law -
  // "errors or discrepancies would of course be higher").
  completed_with_variance: 1,
  sent: 1, draft: 2, fulfillment_generated: 3,
  completed: 8, cancelled: 9,
};
const byUrgencyThenNewest = (a, b) => {
  const ua = SR_URGENCY[a.status] ?? 5;
  const ub = SR_URGENCY[b.status] ?? 5;
  if (ua !== ub) return ua - ub;
  // ORD-3: transitions now stamp updated_at server-side, so "latest
  // activity first" is real, not dead code.
  const ta = new Date(a.updated_at || a.completed_at || a.created_at || 0);
  const tb = new Date(b.updated_at || b.completed_at || b.created_at || 0);
  return tb - ta;
};

export default function StockRequestsPage() {
  const { user, currentBranch, branches } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('inbox');         // inbox | outbox | new
  const [inbox, setInbox] = useState([]);
  const [outbox, setOutbox] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);  // active request id for the side-panel

  const openTransfer = (btoId) => navigate(`/branch-transfers?tab=history&view=${btoId}`);

  // Deep link: /stock-requests?view=<request id> opens that request — the
  // landing point of "From request SR-…" on a transfer. The param is consumed
  // so closing the panel (or a refresh) doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const viewId = searchParams.get('view');
    if (!viewId) return;
    setDetailId(viewId);
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Quick-print straight from a list row (no need to open the request) — the
  // same reflex as the packing slip on /sales-new. Dot-matrix office form.
  const [bizInfo, setBizInfo] = useState({});
  useEffect(() => { api.get('/settings/business-info').then(r => setBizInfo(r.data)).catch(() => {}); }, []);
  const branchNameById = useCallback((id) => branches?.find(b => b.id === id)?.name || '', [branches]);
  const [quickPrinting, setQuickPrinting] = useState('');   // row id being printed
  const quickPrint = useCallback(async (row, format = 'dot_matrix') => {
    setQuickPrinting(row.id);
    let code = row.doc_code || '';
    if (!code) {
      try { code = (await api.post('/doc/generate-code', { doc_type: 'stock_request', doc_id: row.id })).data?.code || ''; }
      catch { /* print without QR if minting fails */ }
    }
    try {
      PrintEngine.print({
        type: 'stock_request',
        data: { ...row, requesting_branch_name: branchNameById(row.requesting_branch_id), supplying_branch_name: branchNameById(row.supplying_branch_id) },
        format, businessInfo: bizInfo, docCode: code,
      });
    } catch (e) { toast.error('Print failed'); }
    setQuickPrinting('');
  }, [bizInfo, branchNameById]);

  const loadLists = useCallback(async () => {
    if (!currentBranch?.id) return;
    setLoading(true);
    try {
      const [inRes, outRes] = await Promise.all([
        api.get(`/stock-requests?branch_id=${currentBranch.id}&role=supplying&limit=50`),
        api.get(`/stock-requests?branch_id=${currentBranch.id}&role=requesting&limit=50`),
      ]);
      setInbox((inRes.data.items || []).slice().sort(byUrgencyThenNewest));
      setOutbox((outRes.data.items || []).slice().sort(byUrgencyThenNewest));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stock requests');
    }
    setLoading(false);
  }, [currentBranch?.id]);

  // ── Supplier-order arrival checks (BT Batch 2 P3) ──
  // Noted supplier lines resurface after 7 days: "Did these arrive?"
  const [arrivalChecks, setArrivalChecks] = useState([]);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [arrivalBusy, setArrivalBusy] = useState(false);
  const [arrivalFollowUp, setArrivalFollowUp] = useState(null); // check being answered "not yet"

  const loadArrivalChecks = useCallback(async () => {
    if (!currentBranch?.id) return;
    try {
      const res = await api.get(`/stock-requests/arrival-checks?branch_id=${currentBranch.id}`);
      const checks = res.data.checks || [];
      setArrivalChecks(checks);
      if (checks.length > 0) setArrivalOpen(true);
    } catch { /* silent — the weekly notification is the fallback */ }
  }, [currentBranch?.id]);

  useEffect(() => { loadArrivalChecks(); }, [loadArrivalChecks]);

  const answerArrival = async (check, outcome) => {
    setArrivalBusy(true);
    try {
      const res = await api.post(`/stock-requests/${check.request_id}/arrival-check`, {
        item_ids: check.items.map(i => i.id),
        outcome,
      });
      if (outcome === 'received') {
        toast.success(`${check.request_number} — items marked received`);
      } else if (outcome === 're_request') {
        toast.success(`Re-requested as ${res.data.re_request?.request_number || 'a new request'}`);
      } else if (outcome === 'keep_waiting') {
        toast.success('We\u2019ll ask again in 7 days');
      } else {
        toast.success(`${check.request_number} — lines closed`);
      }
      setArrivalFollowUp(null);
      const rest = arrivalChecks.filter(c => c.request_id !== check.request_id);
      setArrivalChecks(rest);
      if (rest.length === 0) setArrivalOpen(false);
      loadLists();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record the answer');
    }
    setArrivalBusy(false);
  };

  useEffect(() => { loadLists(); }, [loadLists]);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="stock-requests-page">
      <div className="mb-5"><StockMovementSwitcher current="requests" /></div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList size={22} className="text-teal-600" /> Stock Requests
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Cross-branch fulfillment hub — route lines to Branch Transfer or supplier PO.
          </p>
        </div>
        <Button onClick={() => setTab('new')} className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="new-request-btn">
          <Plus size={14} className="mr-1.5" /> New Request
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox" data-testid="inbox-tab">
            <ArrowRight size={13} className="mr-1.5 rotate-180" />
            Incoming
            {inbox.filter(r => r.status === 'sent').length > 0 && (
              <Badge className="ml-1.5 bg-amber-500 text-white text-[10px]">
                {inbox.filter(r => r.status === 'sent').length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="outbox" data-testid="outbox-tab">
            <ArrowRight size={13} className="mr-1.5" />
            My Requests
          </TabsTrigger>
          <TabsTrigger value="new" data-testid="new-tab">
            <Plus size={13} className="mr-1.5" /> New
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="mt-4">
          <RequestList rows={inbox} loading={loading} onOpen={setDetailId} onOpenTransfer={openTransfer}
                       onQuickPrint={quickPrint} quickPrinting={quickPrinting}
                       emptyLabel="No incoming requests right now." testid="inbox-list" />
        </TabsContent>
        <TabsContent value="outbox" className="mt-4">
          <RequestList rows={outbox} loading={loading} onOpen={setDetailId} onOpenTransfer={openTransfer}
                       onQuickPrint={quickPrint} quickPrinting={quickPrinting}
                       emptyLabel="You haven't sent any requests yet." testid="outbox-list" />
        </TabsContent>
        <TabsContent value="new" className="mt-4">
          <NewRequestForm
            currentBranch={currentBranch} branches={branches}
            onCreated={(id) => { setDetailId(id); loadLists(); setTab('outbox'); }}
          />
        </TabsContent>
      </Tabs>

      {detailId && (
        <DetailDialog
          requestId={detailId}
          currentBranchId={currentBranch?.id}
          onClose={() => { setDetailId(null); loadLists(); }}
          onUpdated={loadLists}
        />
      )}

      {/* ── Supplier-order arrival check (BT Batch 2 P3) ── */}
      {arrivalOpen && arrivalChecks.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="arrival-check-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope' }}>Did your supplier order arrive?</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                These items were sent for supplier ordering over a week ago.
              </p>
            </div>
            <div className="p-5 space-y-4">
              {arrivalChecks.map(check => (
                <div key={check.request_id} className="rounded-lg border border-slate-200 p-3" data-testid={`arrival-check-${check.request_number}`}>
                  <div className="flex items-center justify-between mb-2">
                    <button className="text-xs font-mono font-semibold text-blue-600 hover:underline"
                      onClick={() => { setArrivalOpen(false); setDetailId(check.request_id); }}>
                      {check.request_number}
                    </button>
                    <span className="text-[10px] text-slate-400">sorted {String(check.triaged_at || '').slice(0, 10)}</span>
                  </div>
                  <ul className="text-xs text-slate-700 space-y-0.5 mb-3">
                    {check.items.map(i => (
                      <li key={i.id}>
                        {i.product_name} — {i.qty} {i.unit}
                        {i.supplier_name && <span className="text-slate-400"> · {i.supplier_name}</span>}
                      </li>
                    ))}
                  </ul>
                  {arrivalFollowUp === check.request_id ? (
                    <div className="space-y-1.5" data-testid={`arrival-followup-${check.request_number}`}>
                      <p className="text-[11px] text-slate-600">
                        Not received yet — the owner will be notified. What next?
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        <Button size="sm" className="h-7 text-[11px] bg-teal-600 hover:bg-teal-700 text-white"
                          disabled={arrivalBusy} onClick={() => answerArrival(check, 're_request')}
                          data-testid={`arrival-rerequest-${check.request_number}`}>
                          Re-request items
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          disabled={arrivalBusy} onClick={() => answerArrival(check, 'keep_waiting')}>
                          Keep waiting
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-500"
                          disabled={arrivalBusy} onClick={() => answerArrival(check, 'close')}>
                          Close without receiving
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                          onClick={() => setArrivalFollowUp(null)}>
                          Back
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                        disabled={arrivalBusy} onClick={() => answerArrival(check, 'received')}
                        data-testid={`arrival-received-${check.request_number}`}>
                        Items arrived
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-[11px]"
                        disabled={arrivalBusy} onClick={() => setArrivalFollowUp(check.request_id)}
                        data-testid={`arrival-notyet-${check.request_number}`}>
                        Not yet
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex justify-end">
                <Button size="sm" variant="ghost" className="text-[11px]" onClick={() => setArrivalOpen(false)}>
                  Remind me later
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Request list (used for both inbox and outbox) ────────────────────── */
function RequestList({ rows, loading, onOpen, onOpenTransfer, onQuickPrint, quickPrinting, emptyLabel, testid }) {
  if (loading) return <div className="text-sm text-slate-400 py-10 text-center">Loading…</div>;
  if (!rows.length) return (
    <div className="text-sm text-slate-400 py-10 text-center border-2 border-dashed border-slate-200 rounded-lg" data-testid={`${testid}-empty`}>
      {emptyLabel}
    </div>
  );
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map(r => (
        <Card key={r.id} className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => onOpen(r.id)} data-testid={`request-row-${r.id}`}>
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-sm font-semibold text-slate-800">{r.request_number}</span>
                <StatusBadge status={r.status} items={r.items} />
                {/* The trail forward: the transfer this request produced.
                    Stops propagation so it never opens the request instead. */}
                {r.fulfillment_summary?.bto_id && (
                  <button type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenTransfer?.(r.fulfillment_summary.bto_id); }}
                    className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800 hover:bg-amber-100"
                    title="Open this transfer"
                    data-testid={`request-row-transfer-${r.id}`}>
                    <Truck size={10} /> {r.fulfillment_summary.bto_number || 'Transfer'}
                  </button>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {(r.items || []).length} line(s) · {r.created_by_name || 'Unknown'} ·{' '}
                {fmtDateTime(r.created_at)}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {/* Quick print — stops propagation so it never opens the detail. */}
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-slate-500 hover:text-teal-700"
                title="Quick print (dot matrix)"
                onClick={(e) => { e.stopPropagation(); onQuickPrint?.(r, 'dot_matrix'); }}
                disabled={quickPrinting === r.id}
                data-testid={`quick-print-${r.id}`}>
                {quickPrinting === r.id
                  ? <RefreshCw size={14} className="animate-spin" />
                  : <Printer size={14} />}
              </Button>
              <ArrowRight size={16} className="text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ── New request form ─────────────────────────────────────────────────── */
const newRow = () => ({ id: Date.now() + Math.random(), product: null, qty: '' });

function NewRequestForm({ currentBranch, branches, onCreated }) {
  const [supplyingBranchId, setSupplyingBranchId] = useState('');
  const [notes, setNotes] = useState('');
  // Sales-style row model — each row holds either an unfilled SmartProductSearch
  // OR a filled product card + qty input.  Last row is always blank and auto-
  // grows to a new one when filled, mirroring BranchTransferPage.selectReqProduct.
  const [rows, setRows] = useState([newRow()]);
  const [submitting, setSubmitting] = useState(false);

  // Per-row qty input refs so we can focus-handoff after picking a product
  // (same pattern UnifiedSalesPage / BranchTransferPage use).
  const qtyRefs = useRef({});

  const eligibleBranches = useMemo(
    () => (branches || []).filter(b => b.id !== currentBranch?.id),
    [branches, currentBranch?.id]
  );

  const updateRow = (id, patch) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  const removeRow = (id) =>
    setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  // Mirror BranchTransferPage.selectReqProduct exactly: fill the row, append
  // a fresh row when the last gets filled, then focus this row's qty input.
  const selectProduct = (rowId, p) => {
    // Dedup — refuse to add the same product twice across rows.
    if (rows.some(r => r.product && r.product.id === p.id)) {
      toast.info(`${p.name} is already on the request.`);
      return;
    }
    setRows(prev => {
      const idx = prev.findIndex(r => r.id === rowId);
      if (idx < 0) return prev;
      const next = [...prev];
      // SmartProductSearch (mode='request') gives us {id, name, sku, unit,
      // available, also_branch_stock}. Normalise into our row shape.
      const product = {
        id:                p.id,
        name:              p.name,
        sku:               p.sku || '',
        unit:              p.unit || '',
        supplying_qty:     p.available ?? 0,            // their stock
        requesting_qty:    p.also_branch_stock ?? 0,    // my stock
      };
      next[idx] = { ...next[idx], product, qty: next[idx].qty || '1' };
      if (idx === next.length - 1) next.push(newRow());
      return next;
    });
    setTimeout(() => qtyRefs.current[rowId]?.focus?.(), 60);
  };

  // Tab-from-qty → focus the next row's search input (auto-grow if needed).
  const handleQtyKeyDown = (e, rowIdx) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    const isLastFilled = rowIdx === rows.length - 1 ||
      (rowIdx === rows.length - 2 && !rows[rows.length - 1].product);
    if (isLastFilled) {
      // Let React commit then focus the (now-existing) next row's search.
      setTimeout(() => {
        const nextSearch = document.querySelector(
          `[data-testid="sr-row-search-${rows[rowIdx + 1]?.id || rows[rows.length - 1].id}"] input`
        );
        nextSearch?.focus?.();
      }, 30);
    }
  };

  const resetForm = () => {
    setSupplyingBranchId('');
    setNotes('');
    setRows([newRow()]);
  };

  const submit = async () => {
    if (!supplyingBranchId) { toast.error('Pick a supplying branch first'); return; }
    const filled = rows.filter(r => r.product && parseFloat(r.qty) > 0);
    if (!filled.length) { toast.error('Add at least one product with qty > 0'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/stock-requests', {
        requesting_branch_id: currentBranch.id,
        supplying_branch_id:  supplyingBranchId,
        items: filled.map(r => ({
          product_id:   r.product.id,
          product_name: r.product.name,
          qty:          parseFloat(r.qty) || 0,
          unit:         r.product.unit,
        })),
        notes,
      });
      await api.post(`/stock-requests/${res.data.id}/send`);
      toast.success(`Stock request ${res.data.request_number} sent.`);
      onCreated(res.data.id);
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
    setSubmitting(false);
  };

  const myBranchId = currentBranch?.id;
  const targetBranch = branches?.find(b => b.id === supplyingBranchId);
  const filledCount = rows.filter(r => r.product).length;

  return (
    <div className="space-y-4" data-testid="new-request-form">
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">My Branch</Label>
              <div className="mt-1 px-3 py-2 bg-slate-100 rounded text-sm font-medium">
                {currentBranch?.name || '(no branch selected)'}
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Requesting From</Label>
              <Select value={supplyingBranchId} onValueChange={setSupplyingBranchId}>
                <SelectTrigger className="mt-1" data-testid="supplying-branch-select">
                  <SelectValue placeholder="Pick a branch to request from" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleBranches.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {supplyingBranchId && (
            <div className="space-y-2" data-testid="sr-row-list">
              <div className="grid grid-cols-[1fr_110px_60px_40px] gap-2 text-[10px] uppercase tracking-wider text-slate-400 px-1">
                <span>Product</span>
                <span className="text-center">Qty needed</span>
                <span className="text-center">Unit</span>
                <span></span>
              </div>

              {rows.map((row, idx) => (
                <div key={row.id}
                     className="grid grid-cols-[1fr_110px_60px_40px] gap-2 items-start"
                     data-testid={`sr-row-${row.id}`}>
                  {/* Empty row → SmartProductSearch (typeahead w/ both-branch qty)
                      Filled row → compact summary card + remove btn. */}
                  <div className="relative" data-testid={`sr-row-search-${row.id}`}>
                    {!row.product ? (
                      <SmartProductSearch
                        mode="request"
                        branchId={supplyingBranchId || undefined}
                        alsoBranchId={myBranchId || undefined}
                        onSelect={(p) => selectProduct(row.id, p)}
                        placeholder={idx === 0 ? 'Search product to request…' : 'Add another product…'}
                      />
                    ) : (
                      <div className="h-9 px-3 rounded-md border border-emerald-200 bg-emerald-50/40 flex items-center justify-between gap-3"
                           data-testid={`sr-row-product-${row.id}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate font-medium text-sm">{row.product.name}</span>
                            <span className="text-[10px] text-slate-400 shrink-0">{row.product.sku}</span>
                          </div>
                          <div className="flex gap-3 text-[10px] -mt-0.5">
                            <span className={`font-semibold ${row.product.supplying_qty > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                              {targetBranch?.name || 'Supply'}: {row.product.supplying_qty} {row.product.unit}
                            </span>
                            <span className={`font-semibold ${row.product.requesting_qty > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              You: {row.product.requesting_qty} {row.product.unit}
                            </span>
                          </div>
                        </div>
                        <button type="button"
                                onClick={() => updateRow(row.id, { product: null, qty: '' })}
                                className="text-slate-300 hover:text-rose-500 shrink-0"
                                title="Change product"
                                data-testid={`sr-row-clear-${row.id}`}>
                          <X size={13} />
                        </button>
                      </div>
                    )}
                  </div>

                  <CalcInput
                    ref={(el) => { qtyRefs.current[row.id] = el; }}
                    className="h-9 text-sm text-center"
                    value={row.qty}
                    onChange={(v) => updateRow(row.id, { qty: v })}
                    placeholder="0"
                    disabled={!row.product}
                    onKeyDown={(e) => handleQtyKeyDown(e, idx)}
                    data-testid={`sr-row-qty-${row.id}`}
                  />
                  <span className="h-9 flex items-center justify-center text-xs text-slate-500">
                    {row.product?.unit || '—'}
                  </span>
                  <Button variant="ghost" size="sm" tabIndex={-1}
                          className="h-9 w-9 p-0 text-slate-400 hover:text-rose-500"
                          onClick={() => removeRow(row.id)}
                          disabled={rows.length <= 1}>
                    <X size={14} />
                  </Button>
                </div>
              ))}

              <p className="text-[10px] text-slate-400 italic pt-1">
                Tip: search → Tab → type qty → Tab again → next product. Last empty row auto-grows.
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-600">Notes (optional)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1"
                   placeholder="Any context for the supplying branch…" />
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-500">
              <span className="font-medium">{filledCount}</span> product(s) ready
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-9 px-4" onClick={resetForm}
                      disabled={submitting}>
                Clear
              </Button>
              <Button onClick={submit} disabled={submitting || !filledCount || !supplyingBranchId}
                      className="bg-teal-600 hover:bg-teal-700 text-white h-9 px-6"
                      data-testid="submit-request-btn">
                {submitting ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                Send Request
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// "Aug 7" this year, "Aug 7, 2025" otherwise — a stale cost announces its age.
const fmtTriageDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.length === 10 ? iso + 'T00:00:00' : iso);
    const opts = d.getFullYear() !== new Date().getFullYear()
      ? { month: 'short', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric' };
    return d.toLocaleDateString('en-US', opts);
  } catch { return iso; }
};

/* ── Detail dialog with triage ────────────────────────────────────────── */
function DetailDialog({ requestId, currentBranchId, onClose, onUpdated }) {
  const navigate = useNavigate();
  const { branches } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [assignments, setAssignments] = useState({});  // item_id → assignment
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // ── Printing (same pattern as Branch Transfer: local via PrintEngine,
  //    branch printer via the unified Send-to-Print picker) ──────────────
  const [bizInfo, setBizInfo] = useState({});
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false);
  useEffect(() => {
    api.get('/settings/business-info').then(r => setBizInfo(r.data)).catch(() => {});
  }, []);
  const branchName = useCallback(
    (id) => branches?.find(b => b.id === id)?.name || '',
    [branches]
  );

  // ── Doc code (QR) — minted on first print/share, then reused forever.
  //    The QR resolves to /doc/CODE (read-only owner view; scannable at the
  //    terminal for reprint / cloud print). Same mint endpoint as PO/BT.
  const [docCode, setDocCode] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { if (doc?.doc_code) setDocCode(doc.doc_code); }, [doc?.doc_code]);
  const ensureDocCode = useCallback(async () => {
    if (docCode) return docCode;
    if (doc?.doc_code) { setDocCode(doc.doc_code); return doc.doc_code; }
    if (!doc?.id) return '';
    try {
      const res = await api.post('/doc/generate-code', { doc_type: 'stock_request', doc_id: doc.id });
      const code = res.data?.code || '';
      if (code) setDocCode(code);
      return code;
    } catch {
      toast.error('Could not generate the document code');
      return '';
    }
  }, [doc?.id, doc?.doc_code, docCode]);
  const shareUrl = docCode ? `${window.location.origin}/doc/${docCode}` : '';

  const printRequest = async (format) => {
    if (!doc) return;
    const code = await ensureDocCode();   // prints without QR if minting fails
    PrintEngine.print({
      type: 'stock_request',
      data: {
        ...doc,
        requesting_branch_name: branchName(doc.requesting_branch_id),
        supplying_branch_name: branchName(doc.supplying_branch_id),
      },
      format,
      businessInfo: bizInfo,
      docCode: code,
    });
  };
  const openRemotePrint = async () => { await ensureDocCode(); setSendToPrintOpen(true); };
  const openShare = async () => {
    const code = await ensureDocCode();
    if (code) setShareOpen(true);
  };
  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied');
    } catch { toast.error('Copy failed — select the link and copy it manually'); }
  };
  // Phase 3+ — Timeline
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setTimelineLoading(true);
    try {
      const [res, tlRes] = await Promise.all([
        api.get(`/stock-requests/${requestId}`),
        api.get(`/stock-requests/${requestId}/timeline`),
      ]);
      setDoc(res.data);
      setTimeline(tlRes.data?.events || []);
      // Init blank assignments for triage UI
      const init = {};
      (res.data.items || []).forEach(it => {
        init[it.id] = {
          item_id: it.id,
          fulfillment_type: it.fulfillment_type || '',
          supplier_id: it.supplier_id || '',
          supplier_name: it.supplier_name || '',
          unit_price: 0,
        };
      });
      setAssignments(init);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load request');
    }
    setLoading(false);
    setTimelineLoading(false);
  }, [requestId]);

  useEffect(() => { reload(); }, [reload]);

  // Load suppliers for the REQUESTING branch (PO will live there)
  useEffect(() => {
    if (!doc?.requesting_branch_id) return;
    api.get(`/suppliers?branch_id=${doc.requesting_branch_id}`)
      .then(r => setSuppliers(r.data?.suppliers || r.data || []))
      .catch(() => setSuppliers([]));
  }, [doc?.requesting_branch_id]);

  const isSupplyingBranch = doc && doc.supplying_branch_id === currentBranchId;
  const canTriage = isSupplyingBranch && doc?.status === 'sent';

  // Fulfilling branch's OWN capital per line — the cost basis of the triage
  // decision (transfer it vs order it). Owner ruling 2026-09-02: the requester
  // never sees this; the server 403s anyone outside the supplying branch or
  // without products.view_cost, and we just render nothing.
  const [capInsights, setCapInsights] = useState({});
  useEffect(() => {
    if (!canTriage) { setCapInsights({}); return; }
    api.get(`/stock-requests/${requestId}/capital-insights`)
      .then(r => setCapInsights(r.data?.insights || {}))
      .catch(() => setCapInsights({}));   // 403 = no permission — silent
  }, [canTriage, requestId]);

  // On-hand at the supplying branch — drives "Fill from my stock". Gated only
  // by branch access, so it loads even without the cost permission above.
  const [onHand, setOnHand] = useState(null);   // null = not loaded yet
  useEffect(() => {
    if (!canTriage) { setOnHand(null); return; }
    api.get(`/stock-requests/${requestId}/stock-on-hand`)
      .then(r => setOnHand(r.data?.on_hand || {}))
      .catch(() => setOnHand({}));
  }, [canTriage, requestId]);

  const updateAssign = (item_id, patch) => {
    setAssignments(prev => ({ ...prev, [item_id]: { ...prev[item_id], ...patch } }));
  };

  // "Fill from my stock" — auto-assign Personal Transfer to every line the
  // supplying branch fully covers; leave short/zero lines for a manual call.
  // Nothing fires: the user still reviews and submits with a PIN.
  const [autoResult, setAutoResult] = useState(null);   // {assigned, needsCall}
  const fillFromStock = () => {
    if (!onHand) return;
    // Decide up front (pure) so the counts are reliable — never read a counter
    // mutated inside a setState updater.
    const covered = (doc.items || []).filter(lineFullyCovered);
    const assigned = covered.length;
    const needsCall = (doc.items || []).length - assigned;
    const coveredIds = new Set(covered.map(it => it.id));
    setAssignments(prev => {
      const next = { ...prev };
      (doc.items || []).forEach(it => {
        if (coveredIds.has(it.id)) {
          next[it.id] = { ...next[it.id], item_id: it.id, fulfillment_type: 'transfer' };
        }
      });
      return next;
    });
    setAutoResult({ assigned, needsCall });
    if (assigned === 0) {
      toast.info('No line is fully covered by your stock — decide each one below.');
    } else {
      toast.success(`${assigned} line(s) set to Personal Transfer${needsCall ? ` · ${needsCall} need your call` : ''}`);
    }
  };
  const lineFullyCovered = (it) => {
    if (!onHand) return false;
    const have = parseFloat(onHand[it.product_id] ?? 0) || 0;
    const need = parseFloat(it.qty) || 0;
    return need > 0 && have >= need;
  };

  const submitTriage = async () => {    if (!pin.trim()) { toast.error('PIN required'); return; }
    // Boundary conversion: assignments hold raw string for unit_price
    // so users can type intermediate decimal states like "." or "0."
    // without snap-back. Coerce to number at submission only.
    const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
    const assigns = Object.values(assignments)
      .filter(a => a.fulfillment_type)
      .map(a => ({ ...a, unit_price: toNum(a.unit_price) }));
    if (!assigns.length) { toast.error('Assign at least one line'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/stock-requests/${requestId}/triage`, {
        pin,
        assignments: assigns,
      });
      toast.success(
        `Generated: ${res.data.bto_id ? '1 BTO' : '0 BTO'} + ${res.data.po_ids.length} PO(s)` +
        (res.data.unfulfilled ? ` (${res.data.unfulfilled} unfulfilled)` : '')
      );
      // Instant hand-off: the alerts float drops the "to triage" row and picks
      // up the transfer's own row now, not on its next 60s poll.
      window.dispatchEvent(new CustomEvent('agribooks:alerts-refresh'));
      // Audit SRBT-05: the spawned BTO is a ₱0-priced DRAFT the supplier
      // used to have to hunt down manually — land them on it directly.
      if (res.data.bto_id) {
        toast.info('Transfer draft created — set the transfer prices before sending.', { duration: 9000 });
        onClose();
        navigate(`/branch-transfers?view=${res.data.bto_id}`);
        return;
      }
      reload();
      setPin('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Triage failed');
    }
    setSubmitting(false);
  };

  // (Mark-Ordered write flow retired — P6 trash run; legacy POs display read-only.)

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="request-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList size={18} className="text-teal-600" />
            {doc?.request_number || 'Loading…'}
            {doc && <StatusBadge status={doc.status} items={doc.items} />}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {doc && `Created ${fmtDateTime(doc.created_at)} by ${doc.created_by_name || 'Unknown'}`}
          </DialogDescription>
          {doc && (
            <div className="flex gap-2 pt-1">
              {/* Audit SRBT-08: cancel existed server-side with no UI path */}
              {['draft', 'sent'].includes(doc.status) && (
                <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={async () => {
                    try {
                      await api.post(`/stock-requests/${doc.id}/cancel`, { reason: 'Cancelled from request view' });
                      toast.success('Request cancelled');
                      reload(); onUpdated?.();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
                  }}
                  data-testid="sr-cancel-btn">
                  Cancel request
                </Button>
              )}
              {/* Audit SRBT-02: shorted quantities have a top-up path. Owner
                  2026-09-03: available right after triage too, for the lines
                  marked "can't fulfill" — the requester needn't wait for the
                  truck to re-order what was already declined. */}
              {(['completed', 'completed_with_variance'].includes(doc.status)
                || (doc.status === 'fulfillment_generated' && unfulfilledCount(doc.items) > 0))
                && !doc.re_requested_in && (
                <Button size="sm" variant="outline" className="h-7 text-xs text-teal-700 border-teal-200 hover:bg-teal-50"
                  onClick={async () => {
                    try {
                      const res = await api.post(`/stock-requests/${doc.id}/re-request-remaining`);
                      toast.success(`Draft ${res.data.request_number} created with the outstanding quantities`);
                      reload(); onUpdated?.();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Nothing outstanding to re-request'); }
                  }}
                  data-testid="sr-rerequest-btn">
                  Re-request remaining
                </Button>
              )}
              {doc.re_requested_in && (
                <span className="text-[10px] text-slate-400 self-center">
                  Re-requested as {doc.re_requested_in.request_number}
                </span>
              )}
              {/* Print — same trio as Branch Transfer: local thermal / dot matrix,
                  or send to a paired branch print station */}
              <div className="flex gap-1 ml-auto">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500"
                  onClick={() => printRequest('thermal')} title="Print Thermal"
                  data-testid="sr-print-thermal-btn">
                  <Printer size={13} className="mr-1" /> Thermal
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500"
                  onClick={() => printRequest('dot_matrix')} title="Print Dot Matrix"
                  data-testid="sr-print-dot-btn">
                  <Printer size={13} className="mr-1" /> Dot Matrix
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-emerald-700"
                  onClick={openRemotePrint} title="Send to a print station"
                  data-testid="sr-send-to-print-btn">
                  <Send size={13} className="mr-1" /> Remote
                </Button>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-teal-700"
                  onClick={openShare} title="Show QR / copy link"
                  data-testid="sr-share-btn">
                  <QrCode size={13} className="mr-1" /> Share
                </Button>
              </div>
            </div>
          )}
        </DialogHeader>

        {loading || !doc ? (
          <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* ── Linked child docs banner (post-triage) ─────────────── */}
            {(doc.bto || (doc.pos || []).length > 0) && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-xs space-y-1.5" data-testid="linked-docs">
                <p className="font-semibold text-sky-900">Fulfillment plan</p>
                {doc.bto && (
                  <div className="flex items-center gap-2 text-slate-700">
                    <Truck size={12} /> BTO
                    {/* Audit SRBT-04/07: deep-link to the transfer; label with
                        the TRANSFER lifecycle, not the request badge map
                        (an in-transit BTO used to read 'Awaiting Triage'). */}
                    <button className="text-sky-700 hover:underline font-medium"
                      onClick={() => { onClose(); navigate(`/branch-transfers?view=${doc.bto.id}`); }}
                      data-testid="linked-bto-open">
                      {doc.bto.order_number}
                    </button>
                    <Badge className="text-[10px] bg-slate-100 text-slate-700">
                      {({draft: 'Transfer Draft — set prices', pending_approval: 'Awaiting Approval',
                         sent: 'In Transit', sent_to_terminal: 'On Terminal',
                         received_pending: 'Variance Review', disputed: 'Disputed',
                         returned: 'Bounced Back', received: 'Received',
                         cancelled: 'Cancelled'})[doc.bto.status] || doc.bto.status}
                    </Badge>
                  </div>
                )}
                {(doc.pos || []).map(po => {
                  return (
                    <div key={po.id} className="space-y-1.5" data-testid={`linked-po-${po.id}`}>
                      <div className="flex items-center gap-2 text-slate-700 flex-wrap">
                        <FileText size={12} /> PO <span>{po.po_number}</span>
                        · {po.vendor} · <span>{formatPHP(po.grand_total)}</span>
                        <Badge className="text-[10px] bg-slate-100 text-slate-700">{po.status}</Badge>
                        {po.received_variance_kind && VARIANCE_BADGE[po.received_variance_kind] && (
                          <Badge className={`text-[10px] border ${VARIANCE_BADGE[po.received_variance_kind].cls}`}
                                 data-testid={`variance-badge-${po.id}`}>
                            {po.received_variance_kind === 'completed' ? <Check size={9} className="mr-0.5" /> : <AlertTriangle size={9} className="mr-0.5" />}
                            {VARIANCE_BADGE[po.received_variance_kind].label}
                          </Badge>
                        )}
                      </div>

                      {/* Per-item variance breakdown — shown only when there
                          IS a non-trivial variance (skip 'completed' since
                          everything matched). */}
                      {po.received_variance
                        && po.received_variance_kind !== 'completed'
                        && (po.received_variance.items_variance || []).some(iv => iv.kind !== 'match') && (
                        <div className="bg-white border border-slate-200 rounded text-[10px] ml-5 p-2 space-y-0.5"
                             data-testid={`variance-detail-${po.id}`}>
                          <p className="font-semibold text-slate-600 mb-0.5">Variance vs ordered</p>
                          {po.received_variance.items_variance.filter(iv => iv.kind !== 'match').map((iv, k) => {
                            const tone = iv.kind === 'missing' ? 'text-rose-600'
                                      : iv.kind === 'extra'   ? 'text-violet-600'
                                      : iv.kind === 'under'   ? 'text-amber-600'
                                      : 'text-sky-600';
                            const arrow = iv.delta > 0 ? '+' : '';
                            return (
                              <div key={k} className={`flex justify-between ${tone}`}>
                                <span className="truncate">{iv.product_name}</span>
                                <span className="shrink-0">
                                  {iv.ordered_qty} → {iv.received_qty}
                                  <span className="text-[9px] ml-1">({arrow}{iv.delta})</span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {po.status === 'ordered' && (po.ordered_by_name || po.supplier_ref) && (
                        <p className="text-[10px] text-slate-400 ml-5">
                          Ordered by {po.ordered_by_name || 'Manager'}
                          {po.supplier_ref && <> · ref <span>{po.supplier_ref}</span></>}
                          {po.expected_delivery_date && <> · ETA {po.expected_delivery_date}</>}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Fill from my stock (quick-fulfill) ─────────────────── */}
            {canTriage && onHand && (
              <div className="flex items-center justify-between gap-3 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2" data-testid="fill-from-stock">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-teal-900">Fill from my stock</p>
                  <p className="text-[11px] text-teal-700">
                    Auto-sets Personal Transfer for every line your branch fully covers; you decide the rest.
                  </p>
                  {autoResult && (
                    <p className="text-[11px] text-teal-800 mt-0.5" data-testid="fill-result">
                      {autoResult.assigned} of {(doc.items || []).length} line(s) assigned
                      {autoResult.needsCall > 0 && <> · <span className="font-medium text-amber-700">{autoResult.needsCall} need your call</span></>}
                    </p>
                  )}
                </div>
                <Button size="sm" className="h-8 shrink-0 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                  onClick={fillFromStock} data-testid="fill-from-stock-btn">
                  <Truck size={13} className="mr-1.5" /> Fill from my stock
                </Button>
              </div>
            )}

            {/* ── Items table + triage UI ────────────────────────────── */}
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs" data-testid="detail-items-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Product</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                    {canTriage && <th className="text-left px-3 py-2 font-medium text-slate-600 w-44">Fulfill via</th>}
                    {canTriage && <th className="text-left px-3 py-2 font-medium text-slate-600">Supplier / Price</th>}
                    {!canTriage && <th className="text-left px-3 py-2 font-medium text-slate-600">Status</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(doc.items || []).map(it => {
                    const a = assignments[it.id] || {};
                    return (
                      <tr key={it.id} data-testid={`detail-row-${it.id}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">
                          {it.product_name}
                          {canTriage && capInsights[it.product_id] && (capInsights[it.product_id].last_purchase > 0 || capInsights[it.product_id].moving_average > 0) && (
                            <span className="block text-[10px] font-normal text-slate-400" data-testid={`triage-capital-${it.id}`}>
                              Your LP {capInsights[it.product_id].last_purchase > 0 ? `₱${capInsights[it.product_id].last_purchase.toFixed(2)}` : '—'}
                              {capInsights[it.product_id].last_purchase_date && (
                                <> ({capInsights[it.product_id].last_purchase_is_dr ? 'DR ' : ''}{fmtTriageDate(capInsights[it.product_id].last_purchase_date)})</>
                              )}
                              {' · MA '}{capInsights[it.product_id].moving_average > 0 ? `₱${capInsights[it.product_id].moving_average.toFixed(2)}` : '—'}
                              {' · stock '}{capInsights[it.product_id].stock}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {it.qty} {it.unit}
                          {canTriage && onHand && (
                            <span className={`block text-[10px] font-normal ${lineFullyCovered(it) ? 'text-teal-600' : 'text-amber-600'}`}
                              data-testid={`onhand-${it.id}`}>
                              {(() => {
                                const have = parseFloat(onHand[it.product_id] ?? 0) || 0;
                                return lineFullyCovered(it) ? `✓ ${have} on hand` : `only ${have} on hand`;
                              })()}
                            </span>
                          )}
                        </td>
                        {canTriage ? (
                          <>
                            <td className="px-3 py-2">
                              <Select value={a.fulfillment_type || ''}
                                      onValueChange={(v) => updateAssign(it.id, { fulfillment_type: v })}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`assign-type-${it.id}`}>
                                  <SelectValue placeholder="Pick…" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="transfer">🚚 Personal Transfer</SelectItem>
                                  <SelectItem value="supplier_po">📦 Order from Supplier</SelectItem>
                                  <SelectItem value="unfulfilled">❌ Cannot Fulfill</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-3 py-2">
                              {a.fulfillment_type === 'supplier_po' && (
                                <div className="flex gap-1.5">
                                  <SupplierPicker
                                    suppliers={suppliers}
                                    valueId={a.supplier_id}
                                    valueName={a.supplier_name}
                                    onChange={(sid, sname) =>
                                      updateAssign(it.id, { supplier_id: sid, supplier_name: sname })}
                                    testid={`supplier-picker-${it.id}`}
                                  />
                                  <Input
                                    type="text" inputMode="decimal" placeholder="Price/unit"
                                    value={a.unit_price ?? ''}
                                    onChange={e => updateAssign(it.id, { unit_price: e.target.value })}
                                    className="w-24 h-8 text-xs"
                                    data-testid={`unit-price-${it.id}`}
                                  />
                                </div>
                              )}
                            </td>
                          </>
                        ) : (
                          <td className="px-3 py-2">
                            {it.status === 'noted_for_supplier' ? (
                              <Badge className="text-[10px] bg-violet-100 text-violet-700" data-testid={`noted-badge-${it.id}`}>
                                To order from supplier
                              </Badge>
                            ) : it.status === 'received_from_supplier' ? (
                              <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Received from supplier</Badge>
                            ) : it.status === 'not_received' ? (
                              <Badge className="text-[10px] bg-rose-100 text-rose-700">Not received — re-requested</Badge>
                            ) : it.status === 'closed_not_received' ? (
                              <Badge className="text-[10px] bg-slate-200 text-slate-600">Closed — not received</Badge>
                            ) : (
                              <Badge className="text-[10px] bg-slate-100 text-slate-700">
                                {it.fulfillment_type || 'pending'}
                              </Badge>
                            )}
                            {it.supplier_name && (
                              <span className="text-[10px] text-slate-400 ml-1.5">{it.supplier_name}</span>
                            )}
                            {/* Audit SRBT-06: show what actually arrived per line */}
                            {it.qty_received != null && (
                              <span className={`text-[10px] ml-1.5 font-medium ${parseFloat(it.qty_received) < parseFloat(it.qty || 0) ? 'text-rose-600' : 'text-emerald-600'}`}
                                data-testid={`line-received-${it.id}`}>
                                {it.qty_received}/{it.qty} received
                              </span>
                            )}
                            {it.status === 'unfulfilled' || it.fulfillment_type === 'unfulfilled' ? (
                              <span className="text-[10px] ml-1.5 text-rose-600 font-medium">not fulfilled</span>
                            ) : null}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── PIN + Apply Triage ─────────────────────────────────── */}
            {canTriage && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid="triage-apply">
                <Label className="text-xs text-amber-900 font-semibold">Manager / Branch PIN</Label>
                <div className="flex gap-2">
                  <Input type="password" autoComplete="one-time-code" data-lpignore="true" value={pin} onChange={e => setPin(e.target.value)}
                         placeholder="••••••" className="w-32"
                         data-testid="triage-pin-input" />
                  <Button onClick={submitTriage} disabled={submitting}
                          className="bg-teal-600 hover:bg-teal-700 text-white"
                          data-testid="triage-submit-btn">
                    {submitting ? <RefreshCw size={14} className="animate-spin mr-1.5" /> : <Check size={14} className="mr-1.5" />}
                    Generate Fulfillment Plan
                  </Button>
                </div>
                <p className="text-[10px] text-amber-700">
                  Transfer lines become one branch transfer. Supplier lines are noted
                  for manual ordering — no PO is created. The requesting branch sees
                  what\u2019s coming and confirms arrival in 7 days.
                </p>
              </div>
            )}

            {!canTriage && doc.status === 'sent' && !isSupplyingBranch && (
              <p className="text-xs text-slate-400 italic">Waiting on the supplying branch to triage.</p>
            )}

            {/* ── Activity Timeline (Phase 3+) ────────────────────────── */}
            <Timeline events={timeline} loading={timelineLoading} />
          </>
        )}
      </DialogContent>

      {/* Send to Print — unified picker; the station pulls render data by
          reference (branch names hydrated server-side at pull time) */}
      {doc && (
        <SendToPrintModal
          open={sendToPrintOpen}
          onOpenChange={setSendToPrintOpen}
          documentType="stock_request"
          receiverDocType="stock_request"
          documentName={`Stock Request #${doc.request_number || ''}`}
          documentId={doc.id || ''}
          docCode={docCode || ''}
          referenceNumber={doc.request_number || ''}
          branchId={currentBranchId || ''}
          metadata={{ stock_request_id: doc.id }}
        />
      )}

      {/* Share — QR + link to the read-only /doc view (same POS pattern as
          the Documents page "via phone" QR). Meant for the owner's decision. */}
      {doc && (
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="max-w-sm" data-testid="sr-share-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <QrCode size={18} className="text-teal-600" /> Share {doc.request_number}
              </DialogTitle>
              <DialogDescription className="text-xs">
                Scan or send the link — opens a read-only view of this request. Works with the terminal scanner too.
              </DialogDescription>
            </DialogHeader>
            {docCode ? (
              <div className="text-center space-y-3">
                <div className="bg-white p-3 rounded-xl border inline-block mx-auto">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(shareUrl)}&margin=4`}
                    alt="QR code" className="w-48 h-48" data-testid="sr-share-qr" />
                </div>
                <p className="text-2xl font-mono font-bold tracking-[0.25em] text-slate-800 select-all" data-testid="sr-share-code">
                  {docCode}
                </p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareUrl} className="h-9 text-xs font-mono"
                    onFocus={e => e.target.select()} data-testid="sr-share-url" />
                  <Button size="sm" variant="outline" className="h-9 px-3 shrink-0" onClick={copyShareLink}
                    data-testid="sr-share-copy">
                    <Copy size={14} className="mr-1.5" /> Copy link
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-6 text-center text-sm text-slate-400">Generating code…</div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

/* ── Activity Timeline ────────────────────────────────────────────────── */
const KIND_STYLE = {
  'request.created':   { color: 'bg-slate-400',   Icon: Plus },
  'request.sent':      { color: 'bg-amber-500',   Icon: Send },
  'request.triaged':   { color: 'bg-sky-500',     Icon: Check },
  'request.cancelled': { color: 'bg-rose-500',    Icon: X },
  'request.completed': { color: 'bg-emerald-500', Icon: Check },
  'bto.created':       { color: 'bg-amber-500',   Icon: Truck },
  'bto.sent':          { color: 'bg-amber-600',   Icon: Truck },
  'bto.received':      { color: 'bg-emerald-500', Icon: Check },
  'bto.cancelled':     { color: 'bg-rose-500',    Icon: X },
  'po.created':        { color: 'bg-slate-400',   Icon: FileText },
  'po.ordered':        { color: 'bg-teal-500',    Icon: Send },
  'po.received':       { color: 'bg-emerald-500', Icon: Check },
  'po.cancelled':      { color: 'bg-rose-500',    Icon: X },
};

function Timeline({ events, loading }) {
  if (loading && !events.length) {
    return <div className="text-xs text-slate-400 py-4 text-center">Loading timeline…</div>;
  }
  if (!events.length) return null;
  return (
    <div className="pt-2" data-testid="timeline">
      <p className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
        <RefreshCw size={11} className="text-slate-400" /> Activity
      </p>
      <div className="relative pl-6 space-y-2.5 border-l-2 border-slate-100 ml-2">
        {events.map((e, i) => {
          const style = KIND_STYLE[e.kind] || { color: 'bg-slate-400', Icon: Check };
          const Icon = style.Icon;
          return (
            <div key={i} className="relative" data-testid={`timeline-event-${e.kind}`}>
              <div className={`absolute -left-[27px] top-0.5 w-4 h-4 rounded-full ${style.color} flex items-center justify-center ring-2 ring-white`}>
                <Icon size={9} className="text-white" />
              </div>
              <div className="text-xs">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-medium text-slate-800">{e.label}</span>
                  {e.actor && (
                    <span className="text-[10px] text-slate-500">· {e.actor}</span>
                  )}
                  <span className="text-[10px] text-slate-400 ml-auto">
                    {fmtDateTime(e.at)}
                  </span>
                </div>
                {e.detail && (
                  <p className="text-[11px] text-slate-500 mt-0.5">{e.detail}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Supplier picker (existing + quick-create) ────────────────────────── */
function SupplierPicker({ suppliers, valueId, valueName, onChange, testid }) {
  const [mode, setMode] = useState(valueName && !valueId ? 'quick' : 'list');
  if (mode === 'quick') {
    return (
      <div className="flex gap-1">
        <Input
          value={valueName || ''} onChange={e => onChange('', e.target.value)}
          placeholder="New supplier name" className="w-40 h-8 text-xs"
          data-testid={testid}
        />
        <button onClick={() => { setMode('list'); onChange('', ''); }}
                className="text-slate-400 hover:text-slate-600 px-1">
          <X size={12} />
        </button>
      </div>
    );
  }
  return (
    <div className="flex gap-1">
      <Select value={valueId || ''} onValueChange={(v) => {
        const s = suppliers.find(x => x.id === v);
        onChange(v, s?.name || '');
      }}>
        <SelectTrigger className="h-8 text-xs w-40" data-testid={testid}>
          <SelectValue placeholder="Supplier…" />
        </SelectTrigger>
        <SelectContent>
          {suppliers.length === 0 && (
            <div className="px-2 py-1.5 text-[11px] text-slate-400">No suppliers — use Quick Create</div>
          )}
          {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <button type="button" onClick={() => setMode('quick')}
              className="text-[10px] text-teal-600 hover:underline px-1 whitespace-nowrap">
        + new
      </button>
    </div>
  );
}
